import { ChangeDetectionStrategy, Component, computed, inject, Input } from '@angular/core';
import { IonNav, ModalController } from '@ionic/angular';
import { faArrowLeft, faChevronRight, faXmark } from '@fortawesome/pro-solid-svg-icons';
import { faFileLines, faSignature } from '@fortawesome/pro-solid-svg-icons';
import { TranslocoPipe } from '@jsverse/transloco';
import { FullPageLayoutComponent } from '@app/core/layout/full-page-layout/full-page-layout.component';
import { SharedModule } from '@app/shared/shared.module';
import { ButtonComponent } from '@app/shared/ui/button/button.component';
import { SignDocumentBatchSandbox } from '@app/document-bundle-signing/sandboxes/sign-document-batch.sandbox';
import { SignDocumentBatchVerificationPageComponent } from '../verification-page/verification-page.component';

@Component({
  selector: 'c-sign-document-batch-overview',
  imports: [FullPageLayoutComponent, ButtonComponent, SharedModule, TranslocoPipe],
  templateUrl: './overview-page.component.html',
  styleUrl: './overview-page.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class SignDocumentBatchOverviewPageComponent {
  // Supplied by the page that pushes this overview. Kept as a callback so the
  // overview does not import SignDocumentPageComponent (which imports this
  // overview), avoiding a module cycle.
  @Input() onOpenDocument?: (index: number) => void;

  private readonly sandbox = inject(SignDocumentBatchSandbox);
  private readonly nav = inject(IonNav);
  private readonly modalCtrl = inject(ModalController);

  readonly backIcon = faArrowLeft;
  readonly closeIcon = faXmark;
  readonly fileIcon = faFileLines;
  readonly chevronIcon = faChevronRight;
  readonly signIcon = faSignature;

  readonly documents = computed(() =>
    this.sandbox.documents().map((document, index) => ({
      index,
      name: document.name,
      agreed: this.sandbox.isAgreed(document.documentId),
    }))
  );
  readonly total = computed(() => this.sandbox.documents().length);
  readonly remaining = this.sandbox.remainingCount;
  readonly allAgreed = this.sandbox.allAgreed;

  open(index: number): void {
    // Open the document in review mode on top of the overview: the user can only
    // re-read/approve this one document and return, not walk forward to the next.
    this.onOpenDocument?.(index);
  }

  sign(): void {
    this.sandbox.startVerification();
    this.nav.push(SignDocumentBatchVerificationPageComponent);
  }

  back(): void {
    this.nav.pop();
  }

  close(): void {
    this.modalCtrl.dismiss();
  }
}
